import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { formatDate } from 'lib/utils';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { AI_CREDITS_METER } from 'features/credits/constants/credits';
import { useGetUsersQuery } from 'features/iam/hooks/use-iam';
import { formatMinor, PlanSeats } from 'features/subscription/utils/seat-pools';

const SHOWN_PEOPLE = 5;

interface CancelPlanDialogProps {
  entry: PlanSeats;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  onConfirm: () => void;
}

/** Figma 10827:26228: cancel a plan at the period end, showing who loses it and what the bill drops by. */
export function CancelPlanDialog({ entry, open, onOpenChange, isLoading = false, onConfirm }: CancelPlanDialogProps) {
  const { t } = useTranslation();
  const currentUserId = useCommonStore((state) => state.user?.id);
  const holderIds = entry.pool?.members.seats.map((seat) => seat.userId) ?? [];
  const { data: users } = useGetUsersQuery({
    page: 0,
    pageSize: Math.max(holderIds.length, 1),
    filter: { userIds: holderIds },
    enabled: open && holderIds.length > 0,
  });

  const date = formatDate(entry.periodEndUtc);
  const monthlyCredits = entry.plan?.meters.find((meter) => meter.meterKey === AI_CREDITS_METER)?.includedQuantity;
  const interval = entry.interval?.toLowerCase();
  const billDrop =
    entry.unitAmountMinor && entry.seatsNextPeriod > 0
      ? `${formatMinor(entry.seatsNextPeriod * entry.unitAmountMinor, entry.currencyCode)}${
          interval ? ` / ${t(`INTERVAL_${interval.toUpperCase()}`, interval)}` : ''
        }`
      : '';

  // You first, then everyone else by name; a holder the user list did not return is left out.
  const names = new Map((users?.data ?? []).map((user) => [user.itemId, [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email]));
  const people = [
    ...(currentUserId && holderIds.includes(currentUserId) ? [t('YOU', 'You')] : []),
    ...holderIds.filter((id) => id !== currentUserId).flatMap((id) => (names.has(id) ? [names.get(id) as string] : [])),
  ];
  const moreCount = entry.held - Math.min(people.length, SHOWN_PEOPLE);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isLoading && onOpenChange(isOpen)}>
      <DialogContent className="w-[calc(100vw-2rem)] gap-5 rounded-2xl p-6 sm:max-w-lg sm:p-7">
        <DialogHeader className="space-y-1 pr-8 text-left">
          <DialogTitle className="text-lg font-medium tracking-normal text-grey-950 dark:text-foreground">
            {t('CANCEL_PLAN_TITLE', { plan: entry.name, defaultValue: 'Cancel {{plan}}?' })}
          </DialogTitle>
          <DialogDescription className="text-sm font-normal leading-body text-grey-500 dark:text-muted-foreground">
            {t('CANCEL_PLAN_SEATS_STAY_ACTIVE', {
              seats: entry.purchased,
              plan: entry.name,
              date,
              defaultValue: 'Your {{seats}} {{plan}} seats stay active until {{date}}. Nothing changes before then.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <section className="rounded-section border border-grey-100 dark:border-border">
            <p className="border-b border-grey-100 px-4 py-3 text-sm font-medium leading-body text-grey-950 dark:border-border dark:text-foreground">
              {t('ON_DATE', { date, defaultValue: 'On {{date}}' })}
            </p>

            {entry.held > 0 && (
              <div className="flex flex-col gap-2.5 border-b border-grey-100 px-4 py-3.5 dark:border-border">
                <p className="text-sm leading-body text-grey-950 dark:text-foreground">
                  {entry.held === 1
                    ? t('CANCEL_PLAN_ONE_PERSON_LOSES', { plan: entry.name, defaultValue: '1 person loses their {{plan}} plan' })
                    : t('CANCEL_PLAN_PEOPLE_LOSE', {
                        count: entry.held,
                        plan: entry.name,
                        defaultValue: '{{count}} people lose their {{plan}} plan',
                      })}
                </p>
                <p className="text-sm leading-body text-grey-600 dark:text-muted-foreground">
                  {monthlyCredits != null
                    ? t('CANCEL_PLAN_KEEP_ACCOUNT_CREDITS', {
                        credits: monthlyCredits.toLocaleString(),
                        defaultValue:
                          'They keep their account, without {{credits}} credits a month. Anyone with access to shared credits can still use those.',
                      })
                    : t('CANCEL_PLAN_KEEP_ACCOUNT', {
                        defaultValue:
                          "They keep their account, without the plan's credits. Anyone with access to shared credits can still use those.",
                      })}
                </p>
                {people.length > 0 && (
                  <ul className="flex flex-wrap items-center gap-1.5">
                    {people.slice(0, SHOWN_PEOPLE).map((name) => (
                      <li
                        key={name}
                        className="rounded-full bg-grey-100 px-2.5 py-0.5 text-xs leading-heading text-grey-900 dark:bg-muted dark:text-foreground"
                      >
                        {name}
                      </li>
                    ))}
                    {moreCount > 0 && (
                      <li className="px-1.5 py-0.5 text-xs leading-heading text-grey-600 dark:text-muted-foreground">
                        {t('AND_COUNT_MORE', { count: moreCount, defaultValue: 'and {{count}} more' })}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}

            {billDrop && (
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3.5 text-sm text-grey-950 dark:text-foreground">
                <span className="leading-body">{t('YOUR_BILL_GOES_DOWN_BY', 'Your bill goes down by')}</span>
                <span className="font-medium">{billDrop}</span>
              </div>
            )}
          </section>

          <p className="text-sm leading-body text-grey-600 dark:text-muted-foreground">
            {t('CANCEL_PLAN_KEEP_BEFORE', { date, defaultValue: 'You can keep the plan any time before {{date}}.' })}
          </p>
        </div>

        <DialogFooter className="flex-row justify-end gap-2 sm:space-x-0">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="h-10 rounded-full px-4 text-sm font-medium text-grey-900 hover:bg-grey-50 disabled:opacity-50 dark:text-foreground dark:hover:bg-muted"
          >
            {t('KEEP_PLAN', 'Keep plan')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="h-10 rounded-full bg-error-700 px-5 text-sm font-medium text-true-white hover:bg-error-700/90 disabled:opacity-50"
          >
            {t('CANCEL_PLAN', 'Cancel plan')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
